import { Organization, User } from "../model";
import FieldDefinition from "../model/FieldDefinition";
import OrganizationBillingState, {
  StripeCustomerCreation,
  StripeCustomerStatus,
  StripeMeterData,
  StripeValidation,
} from "../model/OrganizationBillingState";
import { OrganizationMember } from "../model/User";
import API from "./base";

const create = (organization: Organization): Promise<Organization> => {
  return new Promise((resolve, reject) => {
    API.post("/organizations", organization).then(
      (result) => resolve(result.data.payload as Organization),
      reject,
    );
  });
};

const get = (organizationId: string): Promise<Organization> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/" + organizationId, { public: true }).then(
      (result) => {
        resolve(result.data.payload as Organization);
      },
      reject,
    );
  });
};

const byKey = (organizationKey: string): Promise<Organization> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/byKey/" + organizationKey, { public: true }).then(
      (result) => {
        resolve(result.data.payload as Organization);
      },
      reject,
    );
  });
};

const update = (organization: Organization): Promise<Organization> => {
  return new Promise((resolve, reject) => {
    API.put("/organizations/" + organization.id, organization).then(
      (result) => {
        resolve(result.data.payload as Organization);
      },
      reject,
    );
  });
};

// Delete not allowed as variable name
const remove = (organizationId: string): Promise<void> => {
  return API.delete("/organizations/" + organizationId);
};

const byOwner = (userId: string): Promise<Array<Organization>> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/byOwner/" + userId).then((result) => {
      resolve(result.data.payload as Array<Organization>);
    }, reject);
  });
};

const getMembers = (
  organizationId: string,
  filter?: string,
): Promise<Array<User>> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/" + organizationId + "/users", {
      params: { filter: filter ? `${filter}` : undefined },
    }).then((result) => {
      resolve(result.data.payload as Array<User>);
    }, reject);
  });
};

const getMeteredSubscriptionInfo = (
  organizationId: string,
  startDate?: Date,
  endDate?: Date,
): Promise<StripeMeterData> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/" + organizationId + "/tracker/meter", {
      params: {
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
      },
    }).then((result) => {
      resolve(result.data.payload as StripeMeterData);
    }, reject);
  });
};

const getBillingState = (
  organizationId: string,
): Promise<OrganizationBillingState> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/" + organizationId + "/billing").then((result) => {
      resolve(result.data.payload as OrganizationBillingState);
    }, reject);
  });
};

const getSubscriptions = (
  organizationId: string,
): Promise<StripeCustomerStatus> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/" + organizationId + "/subscriptions").then(
      (result) => {
        resolve(result.data.payload as StripeCustomerStatus);
      },
      reject,
    );
  });
};

const getBillingPortalUrl = (
  organizationId: string,
  returnPath: string,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/" + organizationId + "/billing-portal", {
      params: { returnPath: returnPath },
    }).then((result) => {
      resolve(result.data.payload as string);
    }, reject);
  });
};

const forceRefreshSubscription = (organizationId: string): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    API.post(`/organizations/${organizationId}/refresh-subscription`).then(
      (result) => {
        resolve(result.data.payload as boolean);
      },
      reject,
    );
  });
};

const createStripeCustomer = (
  organizationId: string,
  customer: StripeCustomerCreation,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    API.post(`/organizations/${organizationId}/stripe`, customer).then(
      () => resolve(),
      reject,
    );
  });
};

const validateStripeId = (
  organizationId: string,
  stripeId: string,
): Promise<StripeValidation> => {
  return new Promise((resolve, reject) => {
    API.get(
      `/organizations/${organizationId}/stripe/validate/${stripeId}`,
    ).then((result) => {
      resolve(result.data.payload as StripeValidation);
    }, reject);
  });
};

const updateStripeId = (
  organizationId: string,
  stripeId: string | null,
): Promise<Organization> => {
  return API.put(`/organizations/${organizationId}/stripe`, { stripeId });
};

const byMember = (userId: string): Promise<Array<Organization>> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/byMember/" + userId).then((result) => {
      resolve(result.data.payload as Array<Organization>);
    }, reject);
  });
};

const byMemberOrAccountUser = (
  userId: string,
): Promise<Array<Organization>> => {
  return new Promise((resolve, reject) => {
    API.get("/organizations/byMemberOrAccountUser/" + userId).then((result) => {
      resolve(result.data.payload as Array<Organization>);
    }, reject);
  });
};

const removeMember = (
  organizationId: string,
  userId: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    API.delete("/organizations/" + organizationId + "/users/" + userId).then(
      () => resolve(),
      reject,
    );
  });
};

const fromWebsite = (website: string): Promise<Organization> => {
  return new Promise((resolve, reject) => {
    API.post("/organizations/fromWebsite", { url: website }).then(
      (result) => resolve(result.data.payload as Organization),
      reject,
    );
  });
};

const confirmEmail = (
  organizationId: string,
  confirmationCode: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    API.post(`/email-consumer/${organizationId}/confirm`, undefined, {
      params: {
        code: confirmationCode,
      },
    }).then(() => {
      resolve();
    }, reject);
  });
};

const byOrgRole = (orgRoleId: string): Promise<Organization[]> => {
  return new Promise((resolve, reject) => {
    API.get(`/organizations/byOrgRole/${orgRoleId}`).then((result) => {
      resolve(result.data.payload as Organization[]);
    }, reject);
  });
};

const getFieldDefinitions = (
  organizationId: string,
): Promise<{
  selected: string[];
  available: { [key: string]: FieldDefinition };
}> => {
  return new Promise((resolve, reject) => {
    API.get(`/organizations/${organizationId}/fields`).then((result) => {
      resolve(
        result.data.payload as {
          selected: string[];
          available: { [key: string]: FieldDefinition };
        },
      );
    }),
      reject;
  });
};

const addSelectedFieldDefinition = (
  organizationId: string,
  fieldDefinitionId: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    API.post(
      `/organizations/${organizationId}/fields/${fieldDefinitionId}`,
    ).then(() => resolve(), reject);
  });
};

const removeSelectedFieldDefinition = (
  organizationId: string,
  fieldDefinitionId: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    API.delete(
      `/organizations/${organizationId}/fields/${fieldDefinitionId}`,
    ).then(() => resolve(), reject);
  });
};

const setSelectedFieldDefinitions = (
  organizationId: string,
  fieldDefinitionIds: string[],
): Promise<void> => {
  return new Promise((resolve, reject) => {
    API.put(`/organizations/${organizationId}/fields`, fieldDefinitionIds).then(
      () => resolve(),
      reject,
    );
  });
};

const search = (query: string): Promise<Organization[]> => {
  return new Promise((resolve, reject) => {
    API.post("/organizations/search", { query }).then(
      (result) => resolve(result.data.payload as Organization[]),
      reject,
    );
  });
};

const searchMembers = (
  query: string,
  organizationId?: string,
): Promise<OrganizationMember[]> => {
  const endpoint =
    "/organizations/members/search" +
    (organizationId ? "/organization/" + organizationId : "");

  return new Promise((resolve, reject) => {
    API.post(endpoint, { query }).then((result) => {
      resolve(result.data.payload as OrganizationMember[]);
    }, reject);
  });
};

const refreshMembers = (organizationId: string) => {
  const endpoint = `/organizations/${organizationId}/members/refresh`;
  return new Promise((resolve, reject) => {
    API.post(endpoint).then(() => resolve, reject);
  });
};

const getVisibility = (
  organizationId: string,
  userId: string,
): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    API.get(
      `/organizations/members/${userId}/organization/${organizationId}/visibility`,
    ).then((result) => {
      resolve(result.data.payload.visibility as boolean);
    }, reject);
  });
};

const setVisibility = (
  organizationId: string,
  userId: string,
  visibility: boolean,
): Promise<void> => {
  const endpoint = visibility
    ? `/organizations/members/${userId}/organization/${organizationId}/enable`
    : `/organizations/members/${userId}/organization/${organizationId}/disable`;

  return new Promise((resolve, reject) => {
    API.post(endpoint).then(() => resolve(), reject);
  });
};

export default {
  create: create,
  get: get,
  byKey,
  update: update,
  byOwner: byOwner,
  delete: remove,
  getMembers: getMembers,
  getMeteredSubscriptionInfo: getMeteredSubscriptionInfo,
  getBillingState: getBillingState,
  getSubscriptions: getSubscriptions,
  getBillingPortalUrl: getBillingPortalUrl,
  forceRefreshSubscription: forceRefreshSubscription,
  createStripeCustomer: createStripeCustomer,
  validateStripeId: validateStripeId,
  updateStripeId: updateStripeId,
  byMember: byMember,
  byMemberOrAccountUser: byMemberOrAccountUser,
  removeMember: removeMember,
  fromWebsite: fromWebsite,
  confirmEmail: confirmEmail,
  byOrgRole: byOrgRole,
  getFieldDefinitions: getFieldDefinitions,
  addSelectedFieldDefinition: addSelectedFieldDefinition,
  removeSelectedFieldDefinition: removeSelectedFieldDefinition,
  setSelectedFieldDefinitions: setSelectedFieldDefinitions,
  search: search,
  searchMembers: searchMembers,
  getVisibility,
  setVisibility,
  refreshMembers,
};
